import React, { useState, useEffect } from 'react';
import { useTrailWeather } from '../hooks/useTrailWeather';
import '../styles/WeatherAlerts.css';
import { getWeatherCondition, getSeverityColor } from '../utils/weatherCodes';

export default function WeatherAlerts({ coordinates, trailName, trail }) {
    // 🔍 DEBUG
    console.group('🔍 WeatherAlerts - Debug');
    console.log('1. coordinates:', coordinates);
    console.log('2. trail.geometry.type:', trail?.geometry?.type);
    console.log('3. trail.geometry.coordinates:', trail?.geometry?.coordinates);
    console.groupEnd();

    let lat, lon, altitude;
    const [userExperience, setUserExperience] = useState('medio');

    // ========================================
    // GESTIONE COORDINATE BASATA SU geometry.type
    // ========================================

    // Se coordinates è vuoto, prova da trail.geometry
    let coordsToUse = coordinates;
    if (!coordsToUse || (Array.isArray(coordsToUse) && coordsToUse.length === 0)) {
        if (trail?.geometry?.coordinates) {
            console.log('⚠️ coordinates vuoto, uso trail.geometry.coordinates');
            coordsToUse = trail.geometry.coordinates;
        } else {
            console.error('❌ Nessuna coordinata disponibile');
            return (
                <div className="weather-alerts-error">
                    <p>⚠️ Coordinate non disponibili per questo percorso</p>
                </div>
            );
        }
    }

    const geometryType = trail?.geometry?.type;

    // Estrai primo punto in base al tipo di geometria
    let firstPoint;

    if (geometryType === 'Point') {
        // Point: [lon, lat, alt?]
        console.log('📍 Geometry type: Point');
        firstPoint = coordsToUse;
    }
    else if (geometryType === 'LineString') {
        // LineString: [[lon, lat, alt?], [lon, lat, alt?], ...]
        console.log('📍 Geometry type: LineString');
        firstPoint = coordsToUse[0];
    }
    else if (geometryType === 'MultiLineString') {
        // MultiLineString: [[[lon, lat], [lon, lat]], [[lon, lat], [lon, lat]]]
        console.log('📍 Geometry type: MultiLineString');
        firstPoint = coordsToUse[0][0]; // Primo punto del primo segmento
    }
    else {
        // Fallback: prova ad estrarre comunque
        console.warn('⚠️ Geometry type sconosciuto:', geometryType);

        // Naviga fino a trovare un array di numeri
        let current = coordsToUse;
        while (Array.isArray(current) && Array.isArray(current[0])) {
            current = current[0];
        }
        firstPoint = current;
    }

    console.log('📍 Primo punto estratto:', firstPoint);

    if (!firstPoint || !Array.isArray(firstPoint) || firstPoint.length < 2) {
        console.error('❌ Punto non valido:', firstPoint);
        return (
            <div className="weather-alerts-error">
                <p>⚠️ Impossibile estrarre coordinate</p>
                <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                    Tipo: {geometryType || 'sconosciuto'}
                </small>
            </div>
        );
    }

    // Estrai lon, lat, altitude
    [lon, lat, altitude] = firstPoint;

    console.log('📍 lon, lat, alt:', { lon, lat, altitude });

    // Se altitude è undefined/null, cerca in trail.properties
    if (!altitude || altitude === 0) {
        console.log('⚠️ Altitudine mancante, cerco in properties');

        if (trail?.properties?.elevationMin && trail?.properties?.elevationMax) {
            altitude = parseFloat((trail.properties.elevationMin + trail.properties.elevationMax) / 2);
        } else if (trail?.properties?.ele) {
            altitude = parseFloat(trail.properties.ele);
        } else if (trail?.properties?.elevation) {
            altitude = parseFloat(trail.properties.elevation);
        }

        console.log('📊 Altitudine da properties:', altitude);
    }

    // Converti a numero
    lat = parseFloat(lat);
    lon = parseFloat(lon);

    if (altitude) {
        altitude = parseFloat(altitude);
    }

    console.log('🔢 Valori finali:', { lat, lon, altitude });

    // Validazione
    if (isNaN(lat) || isNaN(lon)) {
        console.error('❌ Coordinate NaN:', { lat, lon });
        console.error('   firstPoint era:', firstPoint);
        return (
            <div className="weather-alerts-error">
                <p>⚠️ Coordinate non valide</p>
                <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                    lat={String(lat)}, lon={String(lon)}
                </small>
            </div>
        );
    }

    // Verifica range Italia
    if (lat < 35 || lat > 47 || lon < 6 || lon > 19) {
        console.warn('⚠️ Coordinate fuori Italia:', { lat, lon });
    }

    console.log('✅ Coordinate OK!');

    // Usa hook
    const { weather, loading, error } = useTrailWeather(lat, lon, altitude);

    // Alert Meteoalarm (via TUO proxy)
    const [meteoAlerts, setMeteoAlerts] = useState(null);
    const [meteoLoading, setMeteoLoading] = useState(false);

    // Valanghe AINEVA
    const [avalancheWarning, setAvalancheWarning] = useState(null);

    // Fetch Meteoalarm tramite PROXY BACKEND
    useEffect(() => {
        if (!lat || !lon) return;

        async function fetchMeteoalarm() {
            setMeteoLoading(true);
            try {
                const backendUrl = process.env.REACT_APP_API_URL_NODE;

                // ✅ USA IL TUO PROXY
                const response = await fetch(`${backendUrl}/api/meteoalarm`);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const xmlText = await response.text();

                // Parse XML
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

                // Estrai alert per la regione
                const alerts = parseMeteoalarmXML(xmlDoc, lat, lon);

                if (alerts && alerts.length > 0) {
                    setMeteoAlerts(alerts);
                    console.log('✅ Meteoalarm alerts trovati:', alerts);
                }
            } catch (err) {
                console.error('❌ Meteoalarm fetch error:', err);
            } finally {
                setMeteoLoading(false);
            }
        }

        fetchMeteoalarm();
    }, [lat, lon]);

    // Fetch AINEVA (codice esistente)
    useEffect(() => {
        if (!altitude || altitude < 1500) return;

        async function fetchAvalanche() {
            try {
                const zone = determineAvalancheZone(lat, lon);
                if (!zone) return;

                const response = await fetch(
                    `https://api.aineva.it/v1/bollettini/${zone}`
                );

                if (!response.ok) return;

                const data = await response.json();

                if (data.danger_level >= 3) {
                    setAvalancheWarning(data);
                }
            } catch (err) {
                console.error('❌ AINEVA error:', err);
            }
        }

        fetchAvalanche();
    }, [lat, lon, altitude]);

    // Helper: Parse XML Meteoalarm
    function parseMeteoalarmXML(xmlDoc, lat, lon) {
        try {
            const region = getItalianRegion(lat, lon);
            console.log('🔍 Cercando alert per regione:', region);

            const entries = xmlDoc.getElementsByTagName('entry');
            const alerts = [];

            for (let entry of entries) {
                const titleEl = entry.getElementsByTagName('title')[0];
                const summaryEl = entry.getElementsByTagName('summary')[0];
                const linkEl = entry.getElementsByTagName('link')[0];

                if (!titleEl) continue;

                const title = titleEl.textContent;

                // Filtra per regione e severità critica
                const matchesRegion = title.toLowerCase().includes(region.toLowerCase());
                const isCritical = title.includes('Orange') || title.includes('Red') ||
                    title.includes('Arancione') || title.includes('Rosso');

                if (matchesRegion && isCritical) {
                    alerts.push({
                        event: title,
                        description: summaryEl?.textContent || 'Nessuna descrizione disponibile',
                        severity: (title.includes('Red') || title.includes('Rosso')) ? 'extreme' : 'severe',
                        link: linkEl?.getAttribute('href') || 'https://www.meteoalarm.org'
                    });
                }
            }

            return alerts.length > 0 ? alerts : null;
        } catch (err) {
            console.error('❌ XML parsing error:', err);
            return null;
        }
    }

    // Helper: Determina regione italiana
    function getItalianRegion(lat, lon) {
        // Valle d'Aosta
        if (lat >= 45.4 && lat <= 46.0 && lon >= 6.9 && lon <= 7.9)
            return 'Valle d\'Aosta';

        // Piemonte
        if (lat >= 44.0 && lat <= 46.5 && lon >= 6.6 && lon <= 9.2)
            return 'Piemonte';

        // Liguria
        if (lat >= 43.8 && lat <= 44.7 && lon >= 7.5 && lon <= 10.1)
            return 'Liguria';

        // Lombardia
        if (lat >= 44.6 && lat <= 46.6 && lon >= 8.5 && lon <= 11.5)
            return 'Lombardia';

        // Trentino-Alto Adige
        if (lat >= 45.7 && lat <= 47.1 && lon >= 10.4 && lon <= 12.5)
            return 'Trentino-Alto Adige';

        // Veneto
        if (lat >= 44.8 && lat <= 46.7 && lon >= 10.6 && lon <= 13.0)
            return 'Veneto';

        // Friuli-Venezia Giulia
        if (lat >= 45.6 && lat <= 46.7 && lon >= 12.3 && lon <= 13.9)
            return 'Friuli-Venezia Giulia';

        // Emilia-Romagna
        if (lat >= 43.7 && lat <= 45.1 && lon >= 9.2 && lon <= 12.8)
            return 'Emilia-Romagna';

        // Toscana
        if (lat >= 42.2 && lat <= 44.5 && lon >= 9.7 && lon <= 12.4)
            return 'Toscana';

        // Marche
        if (lat >= 42.7 && lat <= 43.9 && lon >= 12.3 && lon <= 13.9)
            return 'Marche';

        // Umbria
        if (lat >= 42.4 && lat <= 43.6 && lon >= 11.9 && lon <= 13.3)
            return 'Umbria';

        // Lazio
        if (lat >= 40.8 && lat <= 42.8 && lon >= 11.4 && lon <= 13.8)
            return 'Lazio';

        // Abruzzo
        if (lat >= 41.7 && lat <= 42.9 && lon >= 13.0 && lon <= 14.8)
            return 'Abruzzo';

        // Molise
        if (lat >= 41.4 && lat <= 42.0 && lon >= 14.2 && lon <= 15.2)
            return 'Molise';

        // Campania
        if (lat >= 39.9 && lat <= 41.5 && lon >= 13.8 && lon <= 15.8)
            return 'Campania';

        // Puglia
        if (lat >= 39.8 && lat <= 42.2 && lon >= 14.9 && lon <= 18.5)
            return 'Puglia';

        // Basilicata
        if (lat >= 39.9 && lat <= 41.1 && lon >= 15.3 && lon <= 16.9)
            return 'Basilicata';

        // Calabria
        if (lat >= 37.9 && lat <= 40.2 && lon >= 15.6 && lon <= 17.2)
            return 'Calabria';

        // Sicilia
        if (lat >= 36.6 && lat <= 38.3 && lon >= 12.4 && lon <= 15.7)
            return 'Sicilia';

        // Sardegna
        if (lat >= 38.9 && lat <= 41.3 && lon >= 8.1 && lon <= 9.9)
            return 'Sardegna';

        return 'Italia';
    }

    // Helper: Determina zona AINEVA
    function determineAvalancheZone(lat, lon) {
        // Valle d'Aosta
        if (lat > 45.6 && lat < 46 && lon > 6.9 && lon < 7.8)
            return 'valle-aosta';

        // Piemonte Nord
        if (lat > 45.5 && lat < 46.5 && lon > 7 && lon < 8.5)
            return 'piemonte-nord';

        // Trentino
        if (lat > 45.8 && lat < 46.5 && lon > 10.5 && lon < 12)
            return 'trentino';

        // Alto Adige
        if (lat > 46.4 && lat < 47 && lon > 10.5 && lon < 12.5)
            return 'alto-adige';

        // Lombardia
        if (lat > 45.8 && lat < 46.5 && lon > 9 && lon < 11)
            return 'lombardia';

        // Veneto
        if (lat > 45.8 && lat < 46.5 && lon > 11.5 && lon < 12.5)
            return 'veneto';

        return null;
    }

    // Loading states
    if (loading) {
        return (
            <div className="weather-alerts-loading">
                <div className="spinner">⏳</div>
                <p>Caricamento condizioni meteo...</p>
            </div>
        );
    }

    if (error || !weather) return null;

    // Dati oggi
    const today = weather.daily[0];
    const hasSnow = today.snow.depth > 0 || today.snow.snowfall > 0;
    const hasHeavyRain = today.precipitation.sum > 30;
    const hasStrongWind = today.wind.gusts > 50;
    const hasAnyCriticalCondition = hasSnow || hasHeavyRain || hasStrongWind ||
        meteoAlerts || avalancheWarning;

    return (
        <div className="weather-alerts-container">
            {/* ========================================
          ALERT CRITICI METEOALARM
          ======================================== */}

            {meteoAlerts && meteoAlerts.length > 0 && (
                <div className={`alert alert-${meteoAlerts[0].severity === 'extreme' ? 'critical' : 'warning'}`}>
                    <div className="alert-icon">
                        {meteoAlerts[0].severity === 'extreme' ? '🚨' : '⚠️'}
                    </div>
                    <div className="alert-content">
                        <h4>Allerta Meteo Ufficiale</h4>
                        {meteoAlerts.map((alert, i) => (
                            <div key={i} className="alert-item">
                                <strong>{alert.event}</strong>
                                <p>{alert.description}</p>

                                <a href={alert.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="alert-link"
                                >
                                    Maggiori informazioni →
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            )
            }

            {/* ========================================
          BOLLETTINO VALANGHE AINEVA
          ======================================== */}

            {
                avalancheWarning && (
                    <div className="alert alert-avalanche">
                        <div className="alert-icon">⚠️</div>
                        <div className="alert-content">
                            <h4>Pericolo Valanghe - Grado {avalancheWarning.danger_level}/5</h4>
                            <p>{avalancheWarning.description}</p>

                            <a href={avalancheWarning.bulletin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="alert-link"
                            >
                                Leggi bollettino completo →
                            </a>
                        </div>
                    </div>
                )
            }

            {/* ========================================
          CONDIZIONI METEO ATTUALI
          ======================================== */}

            <div className="weather-current">
                <div className="weather-header">
                    <h3>🌤️ Condizioni a {weather.location.elevation}m</h3>
                    <span className="weather-source">Open-Meteo</span>
                </div>

                {/* Temperatura */}
                <div className="weather-item temperature">
                    <div className="weather-icon">🌡️</div>
                    <div className="weather-data">
                        <div className="temp-range">
                            <span className="temp-max">{today.temperature.max.toFixed(1)}°C</span>
                            <span className="temp-separator">/</span>
                            <span className="temp-min">{today.temperature.min.toFixed(1)}°C</span>
                        </div>
                        <div className="temp-subtitle">
                            Percepita: {today.temperature.apparentMin.toFixed(0)}° - {today.temperature.apparentMax.toFixed(0)}°
                        </div>
                    </div>
                </div>

                {/* Neve */}
                {hasSnow && (
                    <div className={`weather-item snow ${today.snow.depth > 30 ? 'snow-warning' : ''}`}>
                        <div className="weather-icon">❄️</div>
                        <div className="weather-data">
                            {today.snow.depth > 0 && (
                                <div className="snow-depth">
                                    <strong>Manto nevoso: {today.snow.depth.toFixed(0)} cm</strong>
                                    {today.snow.depth > 30 && (
                                        <span className="warning-badge">⚠️ Significativo</span>
                                    )}
                                </div>
                            )}
                            {today.snow.snowfall > 0 && (
                                <div className="snow-forecast">
                                    Nevicata prevista: {today.snow.snowfall.toFixed(1)} cm
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Precipitazioni */}
                {today.precipitation.sum > 0 && (
                    <div className={`weather-item precipitation ${hasHeavyRain ? 'precipitation-warning' : ''}`}>
                        <div className="weather-icon">🌧️</div>
                        <div className="weather-data">
                            <div>
                                <strong>{today.precipitation.sum.toFixed(1)} mm</strong>
                                {hasHeavyRain && <span className="warning-badge">⚠️ Abbondanti</span>}
                            </div>
                            <div className="precipitation-prob">
                                Probabilità: {today.precipitation.probability}%
                            </div>
                        </div>
                    </div>
                )}

                {/* Vento */}
                {today.wind.maxSpeed > 20 && (
                    <div className={`weather-item wind ${hasStrongWind ? 'wind-warning' : ''}`}>
                        <div className="weather-icon">💨</div>
                        <div className="weather-data">
                            <div>
                                <strong>Vento: {today.wind.maxSpeed.toFixed(0)} km/h</strong>
                                {hasStrongWind && <span className="warning-badge">⚠️ Forte</span>}
                            </div>
                            {today.wind.gusts > 40 && (
                                <div className="wind-gusts">
                                    Raffiche: {today.wind.gusts.toFixed(0)} km/h
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ========================================
          PREVISIONI PROSSIMI GIORNI
          ======================================== */}

            <div className="weather-forecast">
                <h4>📅 Prossimi 3 giorni</h4>
                <div className="forecast-grid">
                    {weather.daily.slice(1, 4).map((day, i) => {
                        const dayHasSnow = day.snow.snowfall > 0 || day.snow.depth > 0;
                        const dayHasRain = day.precipitation.sum > 5;


                        // ✅ NUOVO: condizione meteo dal weathercode
                        const condition = getWeatherCondition(day.weathercode);
                        // ========================================
                        // VALUTAZIONE ACCESSIBILITÀ PERCORSO
                        // ========================================
                        const getTrailAccessibility = (day, userExperience) => {
                            const reasons = [];

                            // 🚫 CONDIZIONI CRITICHE (UGUALI PER TUTTI)
                            if (
                                day.precipitation.sum > 50 ||
                                day.wind.gusts > 70 ||
                                day.snow.depth > 30 ||
                                day.snow.snowfall > 20 ||
                                day.temperature.min < -10 ||
                                (meteoAlerts && meteoAlerts[0]?.severity === 'extreme') ||
                                (avalancheWarning && avalancheWarning.danger_level >= 3)
                            ) {
                                return {
                                    status: 'sconsigliato',
                                    icon: '❌',
                                    label: 'Percorso sconsigliato',
                                    className: 'trail-not-recommended',
                                    reasons: ['condizioni meteo critiche']
                                };
                            }

                            // ⚠️ CONDIZIONI IMPEGNATIVE
                            const challenging =
                                day.precipitation.sum > 20 ||
                                day.wind.gusts > 40 ||
                                day.snow.depth > 10 ||
                                day.temperature.min < -2 ||
                                (meteoAlerts && meteoAlerts[0]?.severity === 'severe');

                            if (challenging) {

                                // 👤 PRINCIPIANTE → SCONSIGLIATO
                                if (userExperience === 'principiante') {
                                    return {
                                        status: 'sconsigliato',
                                        icon: '❌',
                                        label: 'Percorso sconsigliato per il tuo livello',
                                        className: 'trail-not-recommended',
                                        reasons: [
                                            'condizioni meteo impegnative',
                                            'livello di esperienza non adeguato'
                                        ]
                                    };
                                }

                                // 👤 ESPERTO → ATTENZIONE (linguaggio più neutro)
                                if (userExperience === 'esperto') {
                                    return {
                                        status: 'attenzione',
                                        icon: '⚠️',
                                        label: 'Percorso impegnativo',
                                        className: 'trail-caution',
                                        reasons: [
                                            'condizioni meteo impegnative'
                                        ]
                                    };
                                }

                                // 👤 MEDIO → ATTENZIONE STANDARD
                                return {
                                    status: 'attenzione',
                                    icon: '⚠️',
                                    label: 'Da valutare con attenzione',
                                    className: 'trail-caution',
                                    reasons: [
                                        'condizioni meteo impegnative'
                                    ]
                                };
                            }

                            // ✅ CONDIZIONI FAVOREVOLI
                            return {
                                status: 'accessibile',
                                icon: '✅',
                                label: 'Percorso consigliato',
                                className: 'trail-accessible',
                                reasons: []
                            };
                        };

                        // ========================================
                        // ATTREZZATURA CONSIGLIATA
                        // ========================================
                        const getRequiredEquipment = (day) => {
                            const equipment = [];

                            // 🥾 Scarpe adeguate (sempre)
                            equipment.push({
                                item: 'Scarponi da trekking impermeabili',
                                icon: '🥾',
                                priority: 'essential',
                                // required: 'https://amzn.to/498M0wv'
                            });

                            // 🌧️ Pioggia
                            if (day.precipitation.sum > 5) {
                                equipment.push({
                                    item: 'Giacca impermeabile',
                                    icon: '🧥',
                                    priority: 'essential',
                                    // required: 'https://amzn.to/44IWyB8'
                                });
                            }

                            // 💨 Vento
                            if (day.wind.maxSpeed > 30) {
                                equipment.push({
                                    item: 'Protezione antivento',
                                    icon: '🌬️',
                                    priority: 'essential',
                                    // required: 'https://amzn.to/44DFUTt'
                                });
                            }

                            // ❄️ Freddo
                            if (day.temperature.min < 5) {
                                equipment.push({
                                    item: 'Strato caldo',
                                    icon: '🧤',
                                    priority: 'essential',
                                    // required: 'https://amzn.to/496uIjH'
                                });
                            }

                            if (day.temperature.min < 0) {
                                equipment.push({
                                    item: 'Berretto e guanti caldi',
                                    icon: '🧣',
                                    priority: 'essential',
                                    // required: 'https://amzn.to/495MX8L'
                                });
                            }

                            // 🌧️ Terreno bagnato / instabile
                            if (day.precipitation.sum > 10 || day.snow.depth > 5) {
                                equipment.push({
                                    item: 'Bastoncini da trekking',
                                    icon: '🦯',
                                    priority: 'recommended',
                                    // required: 'https://amzn.to/3KZpMVM'
                                });
                            }

                            // ☀️ Caldo / sole
                            if (day.temperature.max > 25) {
                                equipment.push({
                                    item: 'Cappello e protezione solare',
                                    icon: '🧢',
                                    priority: 'recommended',
                                    // required: 'https://amzn.to/49q0oSj'
                                });
                            }

                            // 🧭 Visibilità ridotta
                            if (day.precipitation.sum > 20 || day.wind.maxSpeed > 40) {
                                equipment.push({
                                    item: 'GPS o mappa offline',
                                    icon: '🧭',
                                    priority: 'recommended',
                                    // required: 'https://amzn.to/4pS3nZu'
                                });
                            }

                            // 🔍 Rimuovi duplicati
                            return equipment.filter(
                                (item, index, self) =>
                                    index === self.findIndex(t => t.item === item.item)
                            );
                        };

                        const accessibility = getTrailAccessibility(day, userExperience);
                        const equipment = getRequiredEquipment(day);

                        return (
                            <div key={i} className={`forecast-day ${accessibility.className}`}>
                                {/* Data */}
                                <div className="forecast-date">
                                    {new Date(day.date).toLocaleDateString('it-IT', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short'
                                    })}
                                </div>
                                {/* ✅ NUOVO: icona + condizione meteo */}
                                <div className="forecast-weather-condition"
                                    style={{ color: getSeverityColor(condition.severity) }}>
                                    <span style={{ fontSize: '28px' }}>{condition.icon}</span>
                                    <span style={{ fontSize: '12px', display: 'block' }}>
                                        {condition.label}
                                    </span>
                                </div>
                                {/* Temperatura */}
                                <div className="forecast-temp">
                                    {day.temperature.max.toFixed(0)}° / {day.temperature.min.toFixed(0)}°
                                </div>

                                {/* Condizioni meteo */}
                                <div className="forecast-conditions">
                                    {dayHasSnow && (
                                        <div className="forecast-condition snow">
                                            ❄️ {day.snow.snowfall > 0 ? `${day.snow.snowfall.toFixed(0)}cm` : 'Neve'}
                                        </div>
                                    )}

                                    {dayHasRain && !dayHasSnow && (
                                        <div className="forecast-condition rain">
                                            🌧️ {day.precipitation.sum.toFixed(0)}mm
                                        </div>
                                    )}

                                    {day.wind.maxSpeed > 30 && (
                                        <div className="forecast-condition wind">
                                            💨 {day.wind.maxSpeed.toFixed(0)}km/h
                                        </div>
                                    )}
                                </div>

                                {/* ========================================
                      ACCESSIBILITÀ PERCORSO
                      ======================================== */}
                                <div className={`trail-accessibility ${accessibility.status}`}>
                                    <div className="accessibility-badge">
                                        <span className="accessibility-icon">{accessibility.icon}</span>
                                        <span className="accessibility-label">{accessibility.label}</span>
                                    </div>
                                </div>

                                {/* ESPERIENZA UTENTE (visibile solo se incide sulla valutazione) */}
                                {accessibility.status !== 'accessibile' && (
                                    <div className="experience-hint">
                                        Valutazione basata su esperienza <strong>{userExperience}</strong>
                                        <button
                                            className="experience-change"
                                            onClick={() => {
                                                const next =
                                                    userExperience === 'principiante'
                                                        ? 'medio'
                                                        : userExperience === 'medio'
                                                            ? 'esperto'
                                                            : 'principiante';
                                                setUserExperience(next);
                                            }}
                                        >
                                            Modifica
                                        </button>
                                    </div>
                                )}

                                {/* ========================================
                      ATTREZZATURA CONSIGLIATA
                      ======================================== */}
                                <div className="equipment-required">
                                    <h5>🎒 Attrezzatura necessaria:</h5>
                                    <ul className="equipment-list">
                                        {equipment.map((item, idx) => (
                                            <li
                                                key={idx}
                                                className={`equipment-item priority-${item.priority}`}
                                            >
                                                <span className="equipment-icon">{item.icon}</span>

                                                <span className="equipment-name">
                                                    {item.item}
                                                </span>

                                                {item.priority === 'critical' && (
                                                    <span className="priority-badge critical">Obbligatorio</span>
                                                )}
                                                {item.priority === 'essential' && (
                                                    <span className="priority-badge essential">Essenziale</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ========================================
          CONSIGLI DI SICUREZZA
          ======================================== */}

            {
                hasAnyCriticalCondition && (
                    <div className="weather-safety">
                        <h4>⚠️ Raccomandazioni di Sicurezza</h4>
                        <ul>
                            {meteoAlerts && (
                                <li>Allerta meteo ufficiale attiva: consulta le previsioni prima di partire</li>
                            )}
                            {today.snow.depth > 30 && (
                                <li>Manto nevoso significativo: verifica condizioni del sentiero</li>
                            )}
                            {today.snow.depth > 50 && (
                                <li>Neve abbondante: equipaggiamento invernale essenziale (ramponi, piccozza)</li>
                            )}
                            {avalancheWarning && (
                                <li>Pericolo valanghe: evita pendii ripidi e consulta bollettino AINEVA</li>
                            )}
                            {hasHeavyRain && (
                                <li>Precipitazioni abbondanti: attenzione a frane e sentieri scivolosi</li>
                            )}
                            {hasStrongWind && (
                                <li>Vento forte: evita crinali esposti e zone rocciose</li>
                            )}
                            {today.temperature.min < 0 && (
                                <li>Temperature sotto zero: attenzione a ghiaccio sul sentiero</li>
                            )}
                        </ul>
                    </div>
                )
            }

            {/* Footer */}
            <div className="weather-footer">
                <small>
                    Ultimo aggiornamento: {new Date(weather.metadata.fetchedAt).toLocaleString('it-IT')}
                </small>
            </div>
        </div>
    );
}